import dayjs, { Dayjs } from "dayjs"
import { Enum, PricingTypes, ProjectStatuses, VisitWorkareaStatus, VisitWorkareaStatuses, withoutUnavailableProjectTypes } from "../../common/constants"
import { CalendarEventVisit, DateRangeViewEvent, DateRangeViewEventWorkArea, DateRangeViewResponse, DayViewEvent, FilterableEvent, ScheduledEvent, Timesheet, Visit, VisitEvent, VisitWorkareaStatusRecord, CrewMember } from "./scheduleApi"
import { findCrewMemberByEmail } from "./crewModel"

export const UNASSIGNED_CREW_ID = 'unassigned' as const

export const getInitials = (name: string): string => {
  const isValid = (char: string) => /^[a-zA-Z0-9]$/.test(char)
  const parts = name.trim().split(/\s+/)

  if (parts.length === 1) {
    const char = parts[0].charAt(0)
    return isValid(char) ? char.toUpperCase() : ''
  }

  const firstInitial = parts[0].charAt(0)
  const lastInitial = parts[parts.length - 1].charAt(0)

  const first = isValid(firstInitial) ? firstInitial.toUpperCase() : ''
  const last = isValid(lastInitial) ? lastInitial.toUpperCase() : ''

  return first + last
}

export const getVisitForEvent = <T extends FilterableEvent>(event: T, calendarEventVisits: CalendarEventVisit[], visits: Visit[], dateRange: DateRange): Visit | null => {
  const eventVisitIds = calendarEventVisits
    .filter(cev => cev.calendarEventId === event.id)
    .map(cev => cev.visitId)

  const visit = visits.find(v => {
    if (!eventVisitIds.includes(v.id)) {
      return false
    }
    const visitDate = dayjs(v.dateUtc)
    return !visitDate.isBefore(dateRange.start, 'day') && !visitDate.isAfter(dateRange.end, 'day')
  })

  return visit ?? null
}

export const getVisitsForEvent = <T extends FilterableEvent>(event: T, calendarEventVisits: CalendarEventVisit[], visits: Visit[]): Visit[] => {
  const eventVisitIds = calendarEventVisits
    .filter(cev => cev.calendarEventId === event.id)
    .map(cev => cev.visitId)

  return visits
    .filter(v => eventVisitIds.includes(v.id))
    .sort((a, b) => dayjs(b.dateUtc).valueOf() - dayjs(a.dateUtc).valueOf())
}

export const defaultVisit = (): Visit => ({
  id: '',
  status: 0,
  internalNotes: null,
  projectId: null,
  dateUtc: '',
})

export const getVisitRemovedWithEvent = (event: Pick<ScheduledEvent, 'createdByVisit'>, eventVisits: Visit[]): Visit | null => {
  if (!event.createdByVisit || eventVisits.length !== 1) {
    return null
  }
  return eventVisits[0]
}

export const eventHasVisit = <T extends FilterableEvent>(event: T, calendarEventVisits: CalendarEventVisit[], visits: Visit[], dateRange: DateRange): boolean => {
  return getVisitForEvent(event, calendarEventVisits, visits, dateRange) !== null
}

export const isEventClockedIn = <T extends FilterableEvent>(
  event: T,
  calendarEventVisits: CalendarEventVisit[],
  visits: Visit[],
  timesheets: Timesheet[],
  crew: CrewMember[],
  dateRange: DateRange
): boolean => {
  const visit = getVisitForEvent(event, calendarEventVisits, visits, dateRange)
  if (!visit) return false

  const crewLead = findCrewMemberByEmail(crew, event.crewForemanEmail)
  if (!crewLead) return false

  const crewLeadTimesheet = timesheets.find(
    t => t.timecardId === visit.id && t.crewMemberId === crewLead.id
  )

  return crewLeadTimesheet !== undefined && crewLeadTimesheet.endDateUtc === null
}

export enum ScheduleMode {
  Day = "day",
  Week = "week",
  Month = "month",
}

export enum EventStatus {
  Planned = 0,
  ClockedIn = 1,
  Visited = 2,
}

export const EventTypes = {
  Job: 'job',
  Task: 'task',
  Consultation: 'consultation',
} as const
export type EventType = Enum<typeof EventTypes>

export const eventTypeOptions = [
  { value: EventTypes.Job, label: 'Jobs' },
  { value: EventTypes.Task, label: 'Tasks' },
  { value: EventTypes.Consultation, label: 'Consultations' },
]

export type ScheduleFilters = {
  crewMemberIds: string[]
  divisionIds: number[]
  projectTypeIds: number[]
  salesPersonIds: string[]
  eventStatuses: EventStatus[]
  eventTypes: EventType[]
  missingVisits: boolean
  visitStatuses: VisitWorkareaStatus[]
}

export const defaultScheduleFilters: ScheduleFilters = {
  crewMemberIds: [],
  divisionIds: [],
  projectTypeIds: [],
  salesPersonIds: [],
  eventStatuses: [EventStatus.Planned],
  eventTypes: [],
  missingVisits: false,
  visitStatuses: [],
}

// Saved filters come back from localStorage and may name a project type the tenant has since
// lost, which would filter the schedule down to nothing. Kept pure so the rehydration path is
// covered without rendering the whole schedule.
export const rehydrateScheduleFilters = (
  savedFilters: Partial<ScheduleFilters>,
  showServiceTickets: boolean
): ScheduleFilters => {
  const rehydrated = { ...defaultScheduleFilters, ...savedFilters }
  return {
    ...rehydrated,
    projectTypeIds: withoutUnavailableProjectTypes(rehydrated.projectTypeIds, showServiceTickets),
  }
}

export type ReorderMode = {
  enabled: boolean
  selectedCrewLeadId: number | typeof UNASSIGNED_CREW_ID | null
}

export type DateRange = {
  start: Dayjs
  end: Dayjs
}

export const clampToDateRange = (date: Dayjs, range: DateRange): Dayjs => {
  if (date.isBefore(range.start, 'day')) {
    return range.start
  }
  if (date.isAfter(range.end, 'day')) {
    return range.end
  }
  return date
}

export const getMonthDateRange = (date: Dayjs): DateRange => ({
  start: date.startOf('month').startOf('week'),
  end: date.endOf('month').endOf('week'),
})

export const getWeekDateRange = (date: Dayjs): DateRange => ({
  start: date.startOf('week'),
  end: date.endOf('week'),
})

export const getDayDateRange = (date: Dayjs): DateRange => ({
  start: date,
  end: date,
})

export type ScheduleState = {
  filters: ScheduleFilters
  view: ScheduleMode
  selectedDate: Dayjs
  visibleDateRange: DateRange
  reorderMode: ReorderMode
}

const today = dayjs()

export const defaultScheduleState: ScheduleState = {
  filters: defaultScheduleFilters,
  view: ScheduleMode.Day,
  selectedDate: today,
  visibleDateRange: getDayDateRange(today),
  reorderMode: {
    enabled: false,
    selectedCrewLeadId: null
  }
}

export type ScheduleAction =
  | { type: 'SET_VIEW'; payload: ScheduleMode }
  | { type: 'SET_DATE'; payload: Dayjs }
  | { type: 'SET_FILTERS'; payload: ScheduleFilters }
  | { type: 'RESET_FILTERS' }
  | { type: 'ENTER_REORDER_MODE'; payload: number | typeof UNASSIGNED_CREW_ID }
  | { type: 'ENTER_REORDER_MODE_FOR_DATE'; payload: { date: Dayjs; crewLeadId: number | typeof UNASSIGNED_CREW_ID } }
  | { type: 'EXIT_REORDER_MODE' }

const getDateRangeForView = (date: Dayjs, view: ScheduleMode): DateRange => {
  if (view === ScheduleMode.Month) {
    return getMonthDateRange(date)
  }
  if (view === ScheduleMode.Week) {
    return getWeekDateRange(date)
  }
  return getDayDateRange(date)
}

export const scheduleFiltersReducer = (state: ScheduleState, action: ScheduleAction): ScheduleState => {
  switch (action.type) {
    case 'SET_VIEW': {
      return {
        ...state,
        view: action.payload,
        visibleDateRange: getDateRangeForView(state.selectedDate, action.payload),
      }
    }
    case 'SET_DATE':
      return {
        ...state,
        selectedDate: action.payload,
        visibleDateRange: getDateRangeForView(action.payload, state.view),
      }
    case 'SET_FILTERS':
      return {
        ...state,
        filters: action.payload
      }
    case 'RESET_FILTERS':
      return {
        ...state,
        filters: defaultScheduleFilters
      }
    case 'ENTER_REORDER_MODE':
      return {
        ...state,
        reorderMode: {
          enabled: true,
          selectedCrewLeadId: action.payload
        }
      }
    case 'ENTER_REORDER_MODE_FOR_DATE':
      return {
        ...state,
        selectedDate: action.payload.date,
        visibleDateRange: getDateRangeForView(action.payload.date, state.view),
        reorderMode: {
          enabled: true,
          selectedCrewLeadId: action.payload.crewLeadId
        }
      }
    case 'EXIT_REORDER_MODE':
      return {
        ...state,
        reorderMode: {
          enabled: false,
          selectedCrewLeadId: null
        }
      }
    default:
      return state
  }
}

export type CrewLeadTimeSummary = {
  billableTime: number
  unbillableTime: number
}

export const calculateCrewLeadTimeSummary = (visitedEvents: ScheduledEvent[]): CrewLeadTimeSummary => {
  if (visitedEvents.length === 0) {
    return { billableTime: 0, unbillableTime: 0 }
  }

  const sortedEvents = [...visitedEvents].sort((a, b) =>
    dayjs(a.start).valueOf() - dayjs(b.start).valueOf()
  )

  let billable = 0
  let unbillable = 0

  sortedEvents.forEach((event, index) => {
    const start = dayjs(event.start)
    const end = dayjs(event.end)
    billable += end.diff(start, 'minute')

    if (index > 0) {
      const prevEvent = sortedEvents[index - 1]
      const prevEnd = dayjs(prevEvent.end)
      const gap = start.diff(prevEnd, 'minute')
      if (gap > 0) {
        unbillable += gap
      }
    }
  })

  return { billableTime: billable, unbillableTime: unbillable }
}

export const formatTimeMinutes = (totalMinutes: number): string => {
  const hours = Math.floor(totalMinutes / 60)
  const mins = Math.floor(totalMinutes % 60)
  if (hours > 0) {
    return `${hours}h ${mins}m`
  }
  return `${mins}m`
}

export type EventWarning = {
  message: string
}

export const getConfiguredWorkAreas = (event: DateRangeViewEvent): DateRangeViewEventWorkArea[] => {
  const parsedIds = event.workAreaIds?.split(",").map(id => parseInt(id.trim())) ?? []
  return (event.workAreas ?? []).filter(wa => parsedIds.includes(wa.id))
}

export const getEventWarning = (
  event: DateRangeViewEvent,
  calendarEventVisits: CalendarEventVisit[],
  workareaStatuses: VisitWorkareaStatusRecord[],
  timesheets: Timesheet[] = []
): EventWarning | null => {
  if (event.rescheduledToEventId != null) {
    return null
  }

  if (!dayjs(event.start).isBefore(dayjs(), 'day')) {
    return null
  }

  const configuredWorkAreas = getConfiguredWorkAreas(event)
  if (configuredWorkAreas.length === 0) {
    return null
  }

  const hasVisit = calendarEventVisits.some(cev => cev.calendarEventId === event.id)
  if (!hasVisit) {
    return { message: "No visit recorded for this event" }
  }

  const visitedWorkAreaIds = new Set([
    ...workareaStatuses.map(s => s.workareaId),
    ...timesheets.filter(t => t.projectWorkareaId).map(t => t.projectWorkareaId!)
  ])

  if (configuredWorkAreas.some(wa => !visitedWorkAreaIds.has(wa.id))) {
    return { message: "Not all work areas have been worked on" }
  }

  return null
}

export const buildEventWarnings = (
  events: DateRangeViewEvent[],
  calendarEventVisits: CalendarEventVisit[],
  eventWorkareaStatuses: Map<number, VisitWorkareaStatusRecord[]>
): Map<number, EventWarning> => {
  const warnings = new Map<number, EventWarning>()
  for (const event of events) {
    const statuses = eventWorkareaStatuses.get(event.id) ?? []
    const warning = getEventWarning(event, calendarEventVisits, statuses)
    if (warning) {
      warnings.set(event.id, warning)
    }
  }
  return warnings
}

export type EventDisplayInfo = {
  workareaStatuses: VisitWorkareaStatusRecord[]
  timesheets: Timesheet[]
  warning: EventWarning | null
  status: EventStatus[]
  isDiscreteVisit: boolean
  areAllWorkAreasComplete: boolean
  rescheduledToDate: string | null
}

const isVisitWorkareaStatusComplete = (status: VisitWorkareaStatus): boolean => {
  return status === VisitWorkareaStatuses.Completed || status === VisitWorkareaStatuses.CompletedUnbillable
}

const isMultiDayEvent = (event: DateRangeViewEvent): boolean => {
  if (event.allDay) {
    return true
  }
  return !dayjs(event.start).isSame(dayjs(event.end), 'day')
}

const isWorkAreaPerVisitOrRecurring = (wa: DateRangeViewEventWorkArea): boolean => {
  return wa.isRecurring || wa.pricingType === PricingTypes.PerVisit
}

export const areAllWorkAreasComplete = (
  event: DateRangeViewEvent,
  workareaStatuses: VisitWorkareaStatusRecord[]
): boolean => {
  const configuredWorkAreas = getConfiguredWorkAreas(event)

  if (configuredWorkAreas.length === 0) {
    if (isMultiDayEvent(event)) {
      return event.allApprovedWorkAreasComplete
    }
    if (workareaStatuses.length === 0) {
      return false
    }
    return workareaStatuses.every(ws => isVisitWorkareaStatusComplete(ws.status))
  }

  const statusByWorkareaId = new Map<number, VisitWorkareaStatus>()
  for (const status of workareaStatuses) {
    statusByWorkareaId.set(status.workareaId, status.status)
  }

  for (const workArea of configuredWorkAreas) {
    if (isWorkAreaPerVisitOrRecurring(workArea)) {
      const visitStatus = statusByWorkareaId.get(workArea.id)
      if (visitStatus === undefined || !isVisitWorkareaStatusComplete(visitStatus)) {
        return false
      }
    } else {
      if (workArea.status !== ProjectStatuses.Completed) {
        return false
      }
    }
  }

  return true
}

export const areWorkAreasCompleteByIds = (
  workAreaIds: number[],
  workareaStatuses: VisitWorkareaStatusRecord[]
): boolean => {
  if (workAreaIds.length === 0) {
    if (workareaStatuses.length === 0) {
      return false
    }
    return workareaStatuses.every(ws => isVisitWorkareaStatusComplete(ws.status))
  }

  const statusByWorkareaId = new Map<number, VisitWorkareaStatus>()
  for (const status of workareaStatuses) {
    statusByWorkareaId.set(status.workareaId, status.status)
  }

  for (const workAreaId of workAreaIds) {
    const status = statusByWorkareaId.get(workAreaId)
    if (status === undefined || !isVisitWorkareaStatusComplete(status)) {
      return false
    }
  }

  return true
}

export const getEventStatuses = <T extends FilterableEvent>(
  event: T,
  visits: Visit[],
  calendarEventVisits: CalendarEventVisit[],
  timesheets: Timesheet[],
  crew: CrewMember[],
  dateRange: DateRange,
  isDiscreteVisit: boolean = false
): EventStatus[] => {
  const statuses: EventStatus[] = []

  if (!event.createdByVisit && !isDiscreteVisit) {
    statuses.push(EventStatus.Planned)
  }

  const visit = getVisitForEvent(event, calendarEventVisits, visits, dateRange)

  if (visit) {
    if (isEventClockedIn(event, calendarEventVisits, visits, timesheets, crew, dateRange)) {
      statuses.push(EventStatus.ClockedIn)
    } else {
      statuses.push(EventStatus.Visited)
    }
  }

  return statuses
}

const statusPriority: Record<EventStatus, number> = {
  [EventStatus.ClockedIn]: 0,
  [EventStatus.Visited]: 1,
  [EventStatus.Planned]: 2,
}

export const getHighestPriorityStatus = (statuses: EventStatus[]): EventStatus => {
  if (statuses.length === 0) {
    return EventStatus.Planned
  }
  return [...statuses].sort((a, b) => statusPriority[a] - statusPriority[b])[0]
}

export const isCrewEditable = (displayInfo: EventDisplayInfo | undefined): boolean => {
  if (!displayInfo) {
    return true
  }
  if (displayInfo.isDiscreteVisit) {
    return false
  }
  return !displayInfo.status.some(s => s === EventStatus.ClockedIn || s === EventStatus.Visited)
}

export const isEventDragDisabled = (
  event: { frozen: boolean },
  displayInfo: EventDisplayInfo | undefined
): boolean => {
  return event.frozen || (displayInfo?.isDiscreteVisit ?? false)
}

export const buildEventDisplayInfo = (
  events: DateRangeViewEvent[],
  calendarEventVisits: CalendarEventVisit[],
  visits: Visit[],
  visitWorkareaStatuses: VisitWorkareaStatusRecord[],
  timesheets: Timesheet[],
  crew: CrewMember[],
  dateRange: DateRange,
  eventMetadata: Map<number, UnifiedEventMetadata>
): Map<number, EventDisplayInfo> => {
  const visitIdsByEvent = new Map<number, Set<string>>()
  for (const cev of calendarEventVisits) {
    const existing = visitIdsByEvent.get(cev.calendarEventId) ?? new Set()
    existing.add(cev.visitId)
    visitIdsByEvent.set(cev.calendarEventId, existing)
  }

  const workareaStatusesByEvent = new Map<number, VisitWorkareaStatusRecord[]>()
  for (const cev of calendarEventVisits) {
    const statuses = visitWorkareaStatuses.filter(s => s.visitId === cev.visitId)
    if (statuses.length > 0) {
      const existing = workareaStatusesByEvent.get(cev.calendarEventId) ?? []
      workareaStatusesByEvent.set(cev.calendarEventId, [...existing, ...statuses])
    }
  }

  const eventsById = new Map(events.map(e => [e.id, e]))

  const map = new Map<number, EventDisplayInfo>()
  for (const event of events) {
    const workareaStatuses = workareaStatusesByEvent.get(event.id) ?? []
    const eventVisitIds = visitIdsByEvent.get(event.id)
    const eventTimesheets = eventVisitIds
      ? timesheets.filter(t => eventVisitIds.has(t.timecardId))
      : []
    const isDiscreteVisit = eventMetadata.get(event.id)?.isDiscreteVisit ?? false
    const rescheduledToDate = event.rescheduledToEventId != null
      ? eventsById.get(event.rescheduledToEventId)?.start ?? null
      : null
    map.set(event.id, {
      workareaStatuses,
      timesheets: eventTimesheets,
      warning: getEventWarning(event, calendarEventVisits, workareaStatuses, eventTimesheets),
      status: getEventStatuses(event, visits, calendarEventVisits, timesheets, crew, dateRange, isDiscreteVisit),
      isDiscreteVisit,
      areAllWorkAreasComplete: areAllWorkAreasComplete(event, workareaStatuses),
      rescheduledToDate,
    })
  }
  return map
}

export type UnifiedEventMetadata = {
  isDiscreteVisit: boolean
  sourceVisitId: string | null
  parentEventId: number | null
}

export type UnifiedEventsResult = {
  events: DateRangeViewEvent[]
  calendarEventVisits: CalendarEventVisit[]
  visits: Visit[]
  eventMetadata: Map<number, UnifiedEventMetadata>
}

const spansMultipleCalendarDays = (event: DateRangeViewEvent): boolean => {
  return !dayjs(event.start).isSame(dayjs(event.end), 'day')
}

const getDiscreteVisitsForEvent = (
  event: DateRangeViewEvent,
  calendarEventVisits: CalendarEventVisit[],
  visitEventsByVisitId: Map<string, VisitEvent>
): VisitEvent[] => {
  const eventVisits = calendarEventVisits
    .filter(cev => cev.calendarEventId === event.id)
    .map(cev => visitEventsByVisitId.get(cev.visitId))
    .filter((ve): ve is VisitEvent => ve !== undefined)
    .sort((a, b) => new Date(a.startUtc).getTime() - new Date(b.startUtc).getTime())

  return spansMultipleCalendarDays(event) ? eventVisits : eventVisits.slice(1)
}

const getOrphanVisitEvents = (
  visitEvents: VisitEvent[],
  linkedVisitIds: Set<string>
): VisitEvent[] => {
  return visitEvents.filter(ve => !linkedVisitIds.has(ve.visitId))
}

export const buildUnifiedEvents = (
  data: DateRangeViewResponse
): UnifiedEventsResult => {
  const resultEvents = [...data.events]
  const resultCalendarEventVisits = [...data.calendarEventVisits]
  const resultVisits = [...data.visits]
  const eventMetadata = new Map<number, UnifiedEventMetadata>()
  let syntheticIdCounter = -1

  const linkedVisitIds = new Set(data.calendarEventVisits.map(cev => cev.visitId))
  const visitEventsByVisitId = new Map(data.visitEvents.map(ve => [ve.visitId, ve]))

  for (const event of data.events) {
    const discreteVisits = getDiscreteVisitsForEvent(event, data.calendarEventVisits, visitEventsByVisitId)

    for (const visitEvent of discreteVisits) {
      const syntheticId = syntheticIdCounter--
      resultEvents.push({ ...visitEvent, id: syntheticId })
      resultCalendarEventVisits.push({ id: syntheticId, calendarEventId: syntheticId, visitId: visitEvent.visitId })
      eventMetadata.set(syntheticId, { isDiscreteVisit: true, sourceVisitId: visitEvent.visitId, parentEventId: event.id })
    }
  }

  for (const visitEvent of getOrphanVisitEvents(data.visitEvents, linkedVisitIds)) {
    const syntheticId = syntheticIdCounter--
    resultEvents.push({ ...visitEvent, id: syntheticId })
    resultCalendarEventVisits.push({ id: syntheticId, calendarEventId: syntheticId, visitId: visitEvent.visitId })
    resultVisits.push({ id: visitEvent.visitId, status: 0, internalNotes: null, projectId: null, dateUtc: visitEvent.startUtc })
    eventMetadata.set(syntheticId, { isDiscreteVisit: true, sourceVisitId: visitEvent.visitId, parentEventId: null })
  }

  return { events: resultEvents, calendarEventVisits: resultCalendarEventVisits, visits: resultVisits, eventMetadata }
}

export const mapDayViewEventToScheduledEvent = (event: DayViewEvent): ScheduledEvent => ({
  ...event,
  customerId: null,
  customerUrl: null,
  projectSold: null,
})

export const mapDateRangeViewEventToScheduledEvent = (event: DateRangeViewEvent): ScheduledEvent => ({
  id: event.id,
  crewForemanEmail: event.crewForemanEmail,
  crewMemberIds: event.crewMemberIds,
  projectType: event.projectType,
  projectAllDivisionIds: event.projectAllDivisionIds,
  salesPersonEmail: event.salesPersonEmail,
  jobName: event.jobName,
  jobNumber: event.jobNumber ?? null,
  jobId: event.jobId,
  startUtc: event.startUtc,
  start: event.start,
  endUtc: event.endUtc,
  end: event.end,
  crewForemanColor: event.crewForemanColor,
  customerId: null,
  customerDisplayName: event.customer?.displayName ?? null,
  customerUrl: null,
  customerAddress1: event.property?.address1 ?? null,
  customerAddress2: event.property?.address2 ?? null,
  customerCity: event.property?.city ?? null,
  customerState: event.property?.state ?? null,
  customerZip: event.property?.zip ?? null,
  customerCounty: event.property?.county ?? null,
  customerCountryCode: event.property?.countryCode ?? null,
  propertyNickname: null,
  projectSold: null,
  workAreaIds: event.workAreaIds,
  workAreaNames: getConfiguredWorkAreas(event).map(wa => wa.name).join(',') || null,
  routeId: event.routeId,
  routeName: null,
  propertyId: event.property?.id ?? null,
  latitude: event.property?.latitude ?? null,
  longitude: event.property?.longitude ?? null,
  repeatingId: event.repeatingId ?? null,
  allDay: event.allDay,
  frozen: event.frozen,
  schedulingNotes: event.schedulingNotes,
  notes: event.notes,
  sendCrewLeadNotifications: event.sendCrewLeadNotifications,
  createdByVisit: event.createdByVisit
})

export const CREW_DAY_SCHEDULES_FEATURE = 'crew-day-schedules'
